import React, { useEffect, useId, useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Bitcoin, RefreshCw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import '../BitcoinChart.css';

const ranges = ['1d', '7d', '30d', '1y'];
const EMPTY_POINTS = [];
const validNumber = value => typeof value === 'number' && Number.isFinite(value);

function chartGeometry(points) {
  const values = points.map(point => point[1]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.14, max * 0.001);
  const low = min - padding;
  const high = max + padding;
  const start = points[0][0];
  const duration = Math.max(points[points.length - 1][0] - start, 1);
  const positions = points.map(([time, price]) => [((time - start) / duration) * 1000, 280 - ((price - low) / (high - low)) * 280]);
  const line = positions.map(([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  return { line, area: `${line} L1000,280 L0,280 Z`, ticks: [high, high - (high - low) / 2, low] };
}

export default function BitcoinChart() {
  const { language } = useLanguage();
  const pl = language === 'pl';
  const [range, setRange] = useState('1d');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const chartId = useId().replace(/:/g, '');
  const locale = pl ? 'pl-PL' : 'en-GB';
  const money = value => validNumber(value) ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }).format(value) : '—';
  const percentage = value => validNumber(value) ? new Intl.NumberFormat(locale, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + '%' : '—';
  const rangeLabels = pl ? ['24 h', '7 dni', '30 dni', '1 rok'] : ['24 h', '7 days', '30 days', '1 year'];

  useEffect(() => {
    let active = true;
    let controller;
    let timer;
    const load = async () => {
      if (document.hidden) return;
      controller?.abort();
      const requestController = new AbortController();
      controller = requestController;
      const timeout = setTimeout(() => requestController.abort(), 12000);
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        const response = await fetch(`${base}/api/btc?range=${range}`, { signal: requestController.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Market data unavailable');
        const data = await response.json();
        if (data.range !== range || !validNumber(data.price) || data.price <= 0 || !validNumber(data.updatedAt)) throw new Error('Invalid market response');
        const series = Array.isArray(data.series) ? data.series.filter(point => Array.isArray(point) && validNumber(point[0]) && validNumber(point[1]) && point[1] > 0) : [];
        if (active && controller === requestController) {
          setSnapshot({ ...data, series });
          setError(false);
        }
      } catch {
        if (active && controller === requestController) {
          setError(true);
          setSnapshot(previous => previous && Date.now() - previous.updatedAt <= 21600000 ? previous : null);
        }
      } finally {
        clearTimeout(timeout);
        if (active && controller === requestController) setLoading(false);
      }
    };
    setSnapshot(null);
    setLoading(true);
    setError(false);
    load();
    timer = setInterval(load, 60000);
    const visible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', visible);
    return () => {
      active = false;
      clearInterval(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', visible);
    };
  }, [range, retry]);

  const points = snapshot?.series || EMPTY_POINTS;
  const geometry = useMemo(() => points.length >= 2 ? chartGeometry(points) : null, [points]);
  const rangeChange = points.length >= 2 ? (points[points.length - 1][1] / points[0][1] - 1) * 100 : null;
  const down = rangeChange < 0;
  const date = timestamp => new Intl.DateTimeFormat(locale, range === '1d' ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', ...(range === '1y' ? { year: '2-digit' } : {}) }).format(timestamp);
  const updated = snapshot ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(snapshot.updatedAt) : '';
  const stale = Boolean(snapshot && (error || snapshot.stale));
  const tickerSource = snapshot?.source?.name === 'CoinPaprika' ? 'CoinPaprika' : 'CoinGecko';
  const tickerSourceUrl = tickerSource === 'CoinPaprika' ? 'https://coinpaprika.com/coin/btc-bitcoin/' : 'https://www.coingecko.com/en/coins/bitcoin';

  return (
    <section id="bitcoin-market" className="tb-bitcoin tb-home-section" aria-labelledby="tb-bitcoin-title">
      <div className="tb-container">
        <div className="tb-section-heading tb-bitcoin-heading">
          <h2 id="tb-bitcoin-title">{pl ? 'Bitcoin. Aktualny kurs.' : 'Bitcoin. The current price.'}</h2>
          <p>{pl ? 'Sprawdź kurs BTC w złotych i jego zmianę w wybranym okresie.' : 'Follow the BTC price in Polish złoty and its change over time.'}</p>
        </div>
        <div className="tb-bitcoin-panel" aria-busy={loading}>
          <div className="tb-bitcoin-top">
            <div className="tb-bitcoin-summary">
              <div className="tb-bitcoin-pair"><span className="tb-bitcoin-symbol"><Bitcoin size={23} strokeWidth={1.6} aria-hidden="true" /></span><span>Bitcoin <span>BTC / PLN</span></span></div>
              <p className={`tb-bitcoin-price${loading ? ' tb-bitcoin-price-loading' : ''}`}>{snapshot ? money(snapshot.price) : loading ? <span className="tb-bitcoin-placeholder" aria-label={pl ? 'Ładowanie ceny' : 'Loading price'} /> : '—'}</p>
              <p className={`tb-bitcoin-change${down ? ' is-negative' : ''}`}>
                {validNumber(rangeChange) ? <>{down ? <ArrowDownRight size={17} aria-hidden="true" /> : <ArrowUpRight size={17} aria-hidden="true" />}<strong>{percentage(rangeChange)}</strong><span>{pl ? 'w wybranym okresie' : 'over selected period'}</span></> : <span>{pl ? 'Cena 1 BTC' : 'Price of 1 BTC'}</span>}
              </p>
            </div>
            <div className="tb-bitcoin-ranges" role="group" aria-label={pl ? 'Zakres wykresu' : 'Chart period'}>{ranges.map((value, index) => <button type="button" key={value} aria-pressed={range === value} onClick={() => setRange(value)}>{rangeLabels[index]}</button>)}</div>
          </div>
          <div className="tb-bitcoin-plot">
            {geometry ? <>
              <div className="tb-bitcoin-canvas">
                <svg viewBox="0 0 1000 280" preserveAspectRatio="none" role="img" aria-labelledby={`${chartId}-title ${chartId}-description`}>
                  <title id={`${chartId}-title`}>{pl ? 'Wykres kursu BTC / PLN' : 'BTC / PLN price chart'}</title>
                  <desc id={`${chartId}-description`}>{`${date(points[0][0])}: ${money(points[0][1])}. ${date(points[points.length - 1][0])}: ${money(points[points.length - 1][1])}.`}</desc>
                  <defs><linearGradient id={`${chartId}-fill`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#41e6b1" stopOpacity="0.17" /><stop offset="100%" stopColor="#41e6b1" stopOpacity="0" /></linearGradient></defs>
                  {[0, 140, 280].map(y => <line key={y} x1="0" y1={y} x2="1000" y2={y} className="tb-bitcoin-gridline" />)}
                  <path d={geometry.area} fill={`url(#${chartId}-fill)`} />
                  <path d={geometry.line} className="tb-bitcoin-line" />
                </svg>
                <div className="tb-bitcoin-x-axis"><span>{date(points[0][0])}</span><span>{date(points[Math.floor(points.length / 2)][0])}</span><span>{date(points[points.length - 1][0])}</span></div>
              </div>
              <div className="tb-bitcoin-y-axis" aria-hidden="true">{geometry.ticks.map((tick, index) => <span key={index}>{new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(tick)}</span>)}</div>
            </> : <div className="tb-bitcoin-state" role="status">
              <p>{loading ? (pl ? 'Pobieranie danych rynkowych…' : 'Loading market data…') : snapshot ? (pl ? 'Historia kursu jest chwilowo niedostępna.' : 'Price history is temporarily unavailable.') : (pl ? 'Dane rynkowe są chwilowo niedostępne.' : 'Market data is temporarily unavailable.')}</p>
              {!loading && <button type="button" className="tb-bitcoin-retry" onClick={() => setRetry(value => value + 1)}><RefreshCw size={15} aria-hidden="true" />{pl ? 'Spróbuj ponownie' : 'Try again'}</button>}
            </div>}
          </div>
          <dl className="tb-bitcoin-stats">
            <div><dt>{pl ? 'Zmiana 24 h' : '24h change'}</dt><dd className={snapshot?.change24h < 0 ? 'is-negative' : ''}>{percentage(snapshot?.change24h)}</dd></div>
            <div><dt>{pl ? 'Maksimum 24 h' : '24h high'}</dt><dd>{money(snapshot?.high24h)}</dd></div>
            <div><dt>{pl ? 'Minimum 24 h' : '24h low'}</dt><dd>{money(snapshot?.low24h)}</dd></div>
            <div><dt>{pl ? 'Wolumen 24 h' : '24h volume'}</dt><dd>{snapshot && validNumber(snapshot.vol24h) ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'PLN', notation: 'compact', maximumFractionDigits: 2 }).format(snapshot.vol24h) : '—'}</dd></div>
          </dl>
          <div className="tb-bitcoin-foot"><p>{snapshot ? `${pl ? 'Dane z' : 'Data as of'} ${updated}` : (pl ? 'Odświeżanie co 60 s' : 'Refreshes every 60 s')}{stale && <span className="tb-bitcoin-stale">{pl ? 'Dane mogą być nieaktualne' : 'Data may be out of date'}</span>}</p><div className="tb-bitcoin-sources"><a href={tickerSourceUrl} target="_blank" rel="noopener noreferrer">Powered by {tickerSource}<ArrowUpRight size={14} aria-hidden="true" /></a>{tickerSource !== 'CoinGecko' && <a href="https://www.coingecko.com/en/coins/bitcoin" target="_blank" rel="noopener noreferrer">{pl ? 'Wykres: CoinGecko' : 'Chart: CoinGecko'}<ArrowUpRight size={14} aria-hidden="true" /></a>}</div></div>
        </div>
      </div>
    </section>
  );
}
